import { useTranslation } from "react-i18next";
import type { ContactRequest } from "types";
import { TableColumn } from "common/components/DataTable/DataTable";
import { CrudFilter } from "common/components/Crud/components/CrudFilters";
import { CrudTab } from "common/components/Crud/components/CrudTabs";
import CrudList from "common/components/Crud/CrudList";
import { useMemo, useState, useCallback } from "react";
import useRouter from "common/hooks/use-router";
import useFetch from "hooks/useFetch";
import TypeBadge from "components/badges/TypeBadge";

const typeColors: Record<string, string> = {
  contact: "#6366f1",
  event: "#f59e0b",
  subscription: "#10b981",
};

const statusColors: Record<string, string> = {
  new: "#ef4444",
  processed: "#22c55e",
};

function ContactRequests() {
  const { t } = useTranslation();
  const { navigate } = useRouter();

  const [countsFilters, setCountsFilters] = useState<Record<string, any>>({});

  const countsUrl = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(countsFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && key !== "type$eq") {
        params.set(key, String(value));
      }
    });
    const qs = params.toString();
    return `contact-requests/data/counts${qs ? `?${qs}` : ""}`;
  }, [countsFilters]);

  const { data: counts } = useFetch<{
    total: number;
    contact: number;
    event: number;
    subscription: number;
  }>(countsUrl);

  const handleFiltersChange = useCallback((filters: Record<string, any>) => {
    setCountsFilters(filters);
  }, []);

  const handleNavigate = (id: any) => {
    navigate(`/contact-requests/${id}`);
  };

  const columns: TableColumn<ContactRequest>[] = useMemo(
    () => [
      {
        field: "type",
        header: t("contact-requests.type"),
        customRender: (rowData: ContactRequest) =>
          rowData.type ? (
            <TypeBadge
              label={t(`contact-requests.type-${rowData.type}`)}
              color={typeColors[rowData.type]}
            />
          ) : (
            <span>-</span>
          ),
      },
      {
        field: "author",
        header: t("contact-requests.author"),
        customRender: (rowData: ContactRequest) => (
          <span className="font-bold">{rowData.author?.username ?? "-"}</span>
        ),
      },
      {
        field: "organization",
        header: t("contact-requests.organization"),
        customRender: (rowData: ContactRequest) => (
          <span>{rowData.organization?.name ?? "-"}</span>
        ),
      },
      {
        field: "status",
        header: t("contact-requests.status"),
        customRender: (rowData: ContactRequest) =>
          rowData.status ? (
            <TypeBadge
              label={t(`contact-requests.status-${rowData.status}`)}
              color={statusColors[rowData.status]}
            />
          ) : (
            <span>-</span>
          ),
      },
      {
        field: "createdAt",
        header: t("contact-requests.createdAt"),
        sortable: true,
        customRender: (rowData: ContactRequest) => (
          <span>
            {rowData.createdAt
              ? new Date(rowData.createdAt).toLocaleString("fr-FR")
              : "-"}
          </span>
        ),
      },
    ],
    [t]
  );

  const tabs: CrudTab[] = useMemo(
    () => [
      { label: t("common.all"), value: null, count: counts?.total },
      {
        label: t("contact-requests.type-contact"),
        value: "contact",
        count: counts?.contact,
      },
      {
        label: t("contact-requests.type-event"),
        value: "event",
        count: counts?.event,
      },
      {
        label: t("contact-requests.type-subscription"),
        value: "subscription",
        count: counts?.subscription,
      },
    ],
    [t, counts]
  );

  const filters: CrudFilter[] = useMemo(() => {
    return [
      {
        name: "status$eq",
        renderType: "switch",
        label: t("contact-requests.onlyNew"),
        switchConfig: {
          onFilter: { name: "status$eq", value: "new" },
          offFilter: { name: "status$eq", value: undefined },
        },
      },
    ];
  }, [t]);

  return (
    <CrudList
      collectionName="contact-requests"
      columns={columns}
      filters={filters}
      tabs={tabs}
      tabsFilterKey="type$eq"
      defaultSort="createdAt:DESC"
      canDelete
      canEdit={false}
      onNavigate={handleNavigate}
      actionsHeader=""
      className="pt-2 px-4"
      onFiltersChange={handleFiltersChange}
    />
  );
}

export default ContactRequests;
